/**
 * Blob-URL LRU for decrypted document media — the generalization of chat's
 * EncryptedAttachment cache (same CACHE_MAX, same revoke-on-evict) keyed by
 * ciphertext URL, shared by every DocImageComponent so re-opening a document
 * (or two components showing the same image) never refetches/redecrypts.
 *
 * Ciphertext is fetched with the WEBVIEW'S OWN fetch(), NEVER the Tauri Rust
 * HTTP adapter/axios: the Rust command returns response bodies as lossy UTF-8
 * strings, which corrupts binary data (see components/EncryptedAttachment.js,
 * where this bit first). The bucket allows GET from any origin — objects are
 * public ciphertext — so CORS is not an issue; the packaged app additionally
 * needs the host in `connect-src` (src-tauri/tauri.conf.json, guarded by
 * mediaCspGuard.test.js).
 *
 * Failure semantics: a rejected fetch/decrypt is NEVER cached — the in-flight
 * entry is dropped, so a transient network failure is retryable by simply
 * calling again (DocImageComponent's Retry button does exactly that).
 */
import { decryptMediaBuffer } from './docMediaCrypto';

export const MEDIA_CACHE_MAX = 50;

const cache = new Map(); // ciphertext url -> object URL (settled successes ONLY)
const inflight = new Map(); // ciphertext url -> Promise<object URL>

function cacheGet(url) {
  if (!cache.has(url)) return null;
  const objectUrl = cache.get(url);
  cache.delete(url);
  cache.set(url, objectUrl); // refresh recency
  return objectUrl;
}

function cachePut(url, objectUrl) {
  if (cache.has(url)) {
    URL.revokeObjectURL(cache.get(url));
    cache.delete(url);
  }
  cache.set(url, objectUrl);
  if (cache.size > MEDIA_CACHE_MAX) {
    const oldest = cache.keys().next().value;
    URL.revokeObjectURL(cache.get(oldest));
    cache.delete(oldest);
  }
}

/**
 * Ciphertext URL + per-attachment key -> Promise of a blob: object URL for the
 * decrypted bytes. Concurrent callers for the same URL share ONE fetch+decrypt
 * (in-flight dedupe); successes land in the LRU, failures reject everyone and
 * leave nothing behind.
 */
export function getDecryptedObjectUrl(url, keyB64, mime) {
  const cached = cacheGet(url);
  if (cached) return Promise.resolve(cached);
  if (inflight.has(url)) return inflight.get(url);

  const promise = (async () => {
    try {
      const res = await fetch(url); // webview fetch ON PURPOSE — see module doc
      if (!res.ok) throw new Error(`media fetch failed (${res.status})`);
      const buf = await res.arrayBuffer();
      const blob = await decryptMediaBuffer(keyB64, buf, mime);
      const objectUrl = URL.createObjectURL(blob);
      cachePut(url, objectUrl);
      return objectUrl;
    } finally {
      // Success is now in the LRU; failure leaves NOTHING behind. Either way
      // the in-flight slot is freed, so the next call is a fresh attempt.
      inflight.delete(url);
    }
  })();

  inflight.set(url, promise);
  return promise;
}
